import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import getApi from '@/lib/axios';
import { ExtractFnReturnType } from '@/lib/react-query';
import { ColumnModel, PaginatedResponse } from '@/features/crm/types';
import { TypesCRM } from '@/types/crm.ts';
import { orderBy } from 'lodash';

// API antiga - retorna todas as colunas com cards
async function getColumns(type: TypesCRM): Promise<ColumnModel[]> {
  const api = await getApi();
  const { data } = await api.get('/crm/colunas/', {
    params: {
      tipo: type,
      include_cards: true // Tenta incluir cards na resposta
    }
  });


  if (data && typeof data === 'object' && 'results' in data) {
    return {
      ...data,
      results: orderBy(data.results, 'ordem')
    }
  }

  // Se for array direto (retrocompatibilidade)
  return {
    ...data,
    results: orderBy(data, 'ordem')
  }
}

// Nova API - listagem leve e paginada (usa o endpoint existente)
async function getStepColumns(
  type: TypesCRM,
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedResponse<ColumnModel>> {
  const api = await getApi();
  const { data } = await api.get('/crm/colunas/', {
    params: {
      tipo: type,
      page,
      page_size: pageSize
    }
  });
  return data;
}

// Nova API - detalhes completos de uma coluna
async function getStepColumnDetail(id: number): Promise<ColumnModel> {
  const api = await getApi();
  const { data } = await api.get(`/crm/colunas/${id}/`);
  return data;
}

type QueryFnType = typeof getColumns;

// Hook antigo - mantido para compatibilidade
function useListColumns(type: TypesCRM) {
  return useQuery<ExtractFnReturnType<QueryFnType>>({
    queryKey: ['fetch-columns', type],
    queryFn: () => getColumns(type),
    enabled: !!type
  });
}

// Novo hook com scroll infinito
function useStepColumns(type: TypesCRM, pageSize: number = 20) {
  return useInfiniteQuery({
    queryKey: ['step-columns', type, pageSize],
    queryFn: ({ pageParam = 1 }) => getStepColumns(type, pageParam, pageSize),
    getNextPageParam: (lastPage) => {
      if (lastPage.next) {
        const url = new URL(lastPage.next);
        return Number(url.searchParams.get('page'));
      }
      return undefined;
    },
    enabled: !!type,
    initialPageParam: 1,
  });
}

// Hook para detalhes de uma coluna
function useStepColumnDetail(id: number) {
  return useQuery({
    queryKey: ['step-column-detail', id],
    queryFn: () => getStepColumnDetail(id),
    enabled: !!id,
  });
}

export {
  getColumns,
  useListColumns,
  getStepColumns,
  useStepColumns,
  getStepColumnDetail,
  useStepColumnDetail
};
